import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { User, Activity, FileText, AlertTriangle, File, Target, Clock, ArrowRight } from 'lucide-react';

const TYPE_CONFIG = {
  snapshot_update: { icon: User, color: 'text-violet-600', bg: 'bg-violet-50' },
  chart: { icon: Activity, color: 'text-sky-600', bg: 'bg-sky-50' },
  note: { icon: FileText, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  incident: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
  document: { icon: File, color: 'text-amber-600', bg: 'bg-amber-50' },
  careplan: { icon: Target, color: 'text-indigo-600', bg: 'bg-indigo-50' },
};

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  } catch { return ''; }
}

export function InlineTimelinePreview({ participantId }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/participants/${participantId}/unified-timeline?limit=5`)
      .then(r => setItems(r.data?.data?.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [participantId]);

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <Card data-testid="inline-timeline-preview">
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
            <Clock className="w-3 h-3" /> Recent Activity
          </p>
          <Button variant="ghost" size="sm" className="h-5 text-[10px] px-1.5" onClick={() => navigate(`/participants/${participantId}?tab=timeline`)} data-testid="view-full-timeline">
            View all <ArrowRight className="w-3 h-3 ml-0.5" />
          </Button>
        </div>
        <div className="space-y-1.5">
          {items.map((item, idx) => {
            const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.note;
            const Icon = cfg.icon;
            return (
              <div key={item.id || idx} className="flex items-center gap-2 text-xs" data-testid={`inline-timeline-${idx}`}>
                <div className={`w-6 h-6 rounded-full ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-3 h-3 ${cfg.color}`} />
                </div>
                <span className="flex-1 truncate">{item.title}</span>
                <span className="text-muted-foreground flex-shrink-0">{timeAgo(item.timestamp)}</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
